import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader/root';
import { compose } from 'redux';

import { TrackEventContext } from 'common/containers/EventContainer';
import LandingFeatureRequestManagementHelmet from 'common/helmets/LandingFeatureRequestManagementHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import withContexts from 'common/util/withContexts';
import FAQs from 'common/www/Faqs';
import LandingCTAButtons from 'common/www/LandingCTAButtons';
import LandingFeaturesCTA from 'common/www/LandingFeaturesCTA';
import { Quote, QuotePerson, Quotes } from 'common/www/Quotes';
import Point5 from 'img/landing/analyze-feedback-3.webp';
import Point1 from 'img/landing/feature-request-tracking-1.webp';
import Point2 from 'img/landing/feature-request-tracking-2.webp';
import Point3 from 'img/landing/feature-request-tracking-3.webp';
import Point4 from 'img/landing/feature-request.webp';
import Jesse from 'img/landing/jesse.webp';
import Philippe from 'img/landing/philippe.webp';
import Saravana from 'img/landing/saravana-small.webp';

import LandingHero from 'img/new-landing-hero.gif';

import 'css/components/www/_LandingSolutionsPage.scss';

class LandingFeatureRequestManagement extends Component {
  static propTypes = {
    trackEvent: PropTypes.func,
  };

  componentDidMount() {
    const { trackEvent } = this.props;
    trackEvent('Viewed Feature Request Management Page');
  }

  render() {
    return (
      <div className="landingFeatureRequestManagement landingSolutionsPage">
        <LandingFeatureRequestManagementHelmet />
        <div className="fullWidthContainer">
          <header className="header">
            <div className="left">
              <div className="text">
                <h1>Feature request software helps you make informed product&nbsp;decisions</h1>
                <p className="subheading">
                  Understand what your customers want so you can build the most
                  impactful&nbsp;features.
                </p>
                <LandingCTAButtons renderDemoButton />
              </div>
            </div>
            <LazyLoadedImage
              loading="eager"
              src={LandingHero}
              alt="Three product managers thinking about feature request management"
            />
          </header>
        </div>
        <main className="mainContentContainer">
          <h2>One dedicated location for feature request&nbsp;management</h2>
          <div className="point">
            <LazyLoadedImage
              loading="eager"
              src={Point1}
              alt="Feature requests organized in a central Canny board"
            />
            <div className="text">
              <h2 className="title">All feature requests in one organized&nbsp;place</h2>
              <p>No more feedback scattered across spreadsheets and&nbsp;conversations.</p>
              <ul>
                <li>
                  Organized user feedback and feature requests means you can actually use them to
                  inform product&nbsp;decisions
                </li>
                <li>Reach out to everyone with the same request and enable&nbsp;discussion</li>
                <li>Close the feedback loop with automated&nbsp;updates</li>
              </ul>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage
              src={Point2}
              alt="Add feedback on behalf of your customers via different sources"
            />
            <div className="text">
              <h2 className="title">Track feature requests where your users&nbsp;are</h2>
              <p>
                Easily capture new feature requests to ensure nothing gets missed. Manage feature
                requests manually or use one of our&nbsp;integrations.
              </p>
              <ul>
                <li>
                  Break down silos between teams; sales and marketing should work in harmony with
                  product&nbsp;development
                </li>
                <li>Set up a feedback board and embed it in your site or&nbsp;app</li>
                <li>
                  Integrate with customer support tools to let your reps capture customer feedback
                  on behalf of&nbsp;users
                </li>
                <li>
                  Integrate with your CRM to enable your sales team to capture customer requests
                  while&nbsp;selling
                </li>
                <li>
                  Create a single feature request collection process across the whole SaaS&nbsp;team
                </li>
              </ul>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage
              src={Point3}
              alt="User profiles in Canny let a product manager see who made a feature request"
            />
            <div className="text">
              <h2 className="title">Identity is&nbsp;key</h2>
              <p>
                Not all customers are created equal. It's important to know who is giving
                you&nbsp;feedback.
              </p>
              <ul>
                <li>
                  Make decisions based on the requests from your target segment and
                  customer&nbsp;needs
                </li>
                <li>Keep track of product feature requests needed to close specific&nbsp;deals</li>
                <li>
                  Users don’t need to log in to give feedback—Canny manages this automatically
                </li>
              </ul>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage
              src={Point4}
              alt="Canny detects existing feature requests so your customers don't create duplicates."
            />
            <div className="text">
              <h2 className="title">Avoid duplicates with feature&nbsp;voting</h2>
              <p>
                Canny automatically detects if a requested feature already exists in your portal. If
                it does, customers can add their feature upvote and leave comments on the
                existing&nbsp;request.
              </p>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage
              src={Point5}
              alt="Comment on posts in Canny to solicit additional feedback or gain clarity"
            />
            <div className="text">
              <h2 className="title">Empower your product&nbsp;team</h2>
              <p>
                Let product managers see all incoming requests, respond to them, prioritize them,
                and then communicate progress to&nbsp;stakeholders.
              </p>
              <ul>
                <li>
                  Discover new product feature requests based on what users actually&nbsp;want
                </li>
                <li>
                  Build product improvements based on feature upvotes and community&nbsp;engagement
                </li>
                <li>
                  Prioritize ideas and add them to a public roadmap to keep customers updated on
                  your&nbsp;progress
                </li>
              </ul>
            </div>
          </div>
          <Quotes
            title={<>Thousands of companies make managing feature requests easy with&nbsp;Canny</>}>
            <Quote
              header={
                <>Document360 manages huge numbers of incoming feature requests with&nbsp;Canny</>
              }
              text={
                <>
                  When we started to see phenomenal growth, the number of feature requests
                  dramatically increased. We couldn’t manage it. What’s more, we couldn’t keep our
                  users updated. That's when we turned to&nbsp;Canny.
                </>
              }
              person={
                <QuotePerson
                  name="Saravana Kumar"
                  image={Saravana}
                  title={<>Founder,&nbsp;Document360</>}
                />
              }
            />
            <Quote
              header={
                <>
                  <>Missive drives product improvement by centralizing feature&nbsp;requests</>
                </>
              }
              text={
                <>
                  Having that central repository of feature requests is super valuable. This
                  repository becomes a decision making tool for the roadmap. It’s also a way to
                  brainstorm about new things and new&nbsp;features.
                </>
              }
              person={
                <QuotePerson
                  name="Philippe Lehoux"
                  image={Philippe}
                  title={<>CEO,&nbsp;Missive</>}
                />
              }
            />
            <Quote
              header={
                <>
                  GiveButter improves customer satisfaction by building the features they
                  care&nbsp;about
                </>
              }
              text={
                <>
                  Canny gives us a strong sense of what customers want – both on the big picture
                  features and “quality of life” ones. The latter makes customers’ daily usage of
                  GiveButter easier and more&nbsp;efficient.
                </>
              }
              person={
                <QuotePerson
                  name="Jesse Sandala"
                  image={Jesse}
                  title={<>Director of Product Management,&nbsp;GiveButter</>}
                />
              }
            />
          </Quotes>
          <LandingFeaturesCTA />
          <LandingFooterCTA style="solid" expanded={true} />
          <FAQs
            faqs={[
              {
                question: 'How does Canny prevent duplicate requests?',
                answer:
                  '<div><p>Our feature request form automatically suggests similar requests to your users based on what they write. That lets them:</p><ul><li>Add their votes to the existing feature request</li><li>Discuss it with other users (and your team)</li><li>Add their suggestions</li></ul></div>',
                html: (
                  <>
                    <p>
                      Our feature request form automatically suggests similar requests to your users
                      based on what they write. That lets them:
                    </p>
                    <ul className="orderedList">
                      <li className="orderedListItem">
                        Add their votes to the existing feature request
                      </li>
                      <li className="orderedListItem">
                        Discuss it with other users (and your team)
                      </li>
                      <li className="orderedListItem">Add their suggestions</li>
                    </ul>
                  </>
                ),
              },
              {
                question: 'Is Canny just a feature request tool?',
                answer:
                  '<p>Nope! We offer a range of product management tools that help you build better products. Managing customer feedback and feature requests is a huge part of our offering. But we also offer tools that let you manage your product roadmap, changelog, and release notes.</p>',
                html: (
                  <p>
                    Nope! We offer a range of product management tools that help you build better
                    products. Managing customer feedback and feature requests is a huge part of our
                    offering. But we also offer tools that let you manage your product roadmap,
                    changelog, and release notes.
                  </p>
                ),
              },
              {
                question: 'How does Canny handle feature request tracking and collection?',
                answer:
                  '<div><p>We offer multiple ways to help you capture every product feature request. First, you can easily create a feature request board where users can add ideas, upvote feature ideas, comment, etc. You can either embed your board in your site or link to it.</p><p>Encouraging users to visit your board to add ideas is a great way to get product feedback.</p><p>We also offer integrations that help you collect feature requests in your other tools. For example, you can integrate with your CRM so that salespeople can add feature ideas directly in the CRM. You can also do this with customer support tools like Intercom; reps can add new ideas (or upvote existing ones) on behalf of customers directly in this tool.</p></div>',
                html: (
                  <>
                    <p>
                      We offer multiple ways to help you capture every product feature request.
                      First, you can easily create a feature request board where users can add
                      ideas, upvote feature ideas, comment, etc. You can either embed your board in
                      your site or link to it.
                    </p>
                    <p>
                      Encouraging users to visit your board to add ideas is a great way to get
                      product feedback.
                    </p>
                    <p>
                      We also offer integrations that help you collect feature requests in your
                      other tools. For example, you can integrate with your CRM so that salespeople
                      can add feature ideas directly in the CRM. You can also do this with customer
                      support tools like Intercom; reps can add new ideas (or upvote existing ones)
                      on behalf of customers directly in this tool.
                    </p>
                  </>
                ),
              },
              {
                question: 'What is feature request software?',
                answer:
                  "<div><p>It's software that handles all aspects of feature request management:</p><ul><li>Collection</li><li>Analysis</li><li>Management</li><li>Prioritization</li><li>Scheduling</li><li>Sharing product updates</li></ul><p>Tools like Canny connect to your other systems. That includes:</p><ul><li>Customer support software</li><li>CRMs</li><li>Project management tools</li></ul><p>This helps to capture and visually showcase feature requests across your entire ecosystem.</p></div>",
                html: (
                  <>
                    <p>It's software that handles all aspects of feature request management:</p>
                    <ul className="orderedList">
                      <li className="orderedListItem">Collection</li>
                      <li className="orderedListItem">Analysis</li>
                      <li className="orderedListItem">Management</li>
                      <li className="orderedListItem">Prioritization</li>
                      <li className="orderedListItem">Scheduling</li>
                      <li className="orderedListItem">Sharing product updates</li>
                    </ul>
                    <p>Tools like Canny connect to your other systems. That includes:</p>
                    <ul className="orderedList">
                      <li className="orderedListItem">Customer support software</li>
                      <li className="orderedListItem">CRMs</li>
                      <li className="orderedListItem">
                        <p>
                          Project management tools
                          <span>
                            This helps to capture and visually showcase feature requests across your
                            entire ecosystem.
                          </span>
                        </p>
                      </li>
                    </ul>
                  </>
                ),
              },
            ]}
          />
        </main>
      </div>
    );
  }
}

export default compose(
  withContexts({ trackEvent: TrackEventContext }),
  hot,
)(LandingFeatureRequestManagement);
